import { useForm } from '@tanstack/react-form';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Textarea,
} from '@virtual-care/shadcn';
import {
  FieldMessage,
  FormMessage,
  TheButton,
  client,
  errorToFormIssue,
} from '@virtual-care/ui';

const schema = z.object({
  name: z.string().min(1, { message: 'Name is required' }),
  nameEn: z.string(),
  description: z.string().min(1, { message: 'Description is required' }),
  descriptionEn: z.string(),
  insuranceCode: z.string().min(1, { message: 'Insurance code is required' }),
});

type Speciality = {
  id: string;
  name: string;
  nameEn?: string | null;
  description: string;
  descriptionEn?: string | null;
  insuranceCode: string;
};

export function UpdateSpecialityDialog({
  speciality,
  trigger,
  open,
  onOpenChange,
}: {
  speciality: Speciality;
  trigger: React.ReactNode;
  open?: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();

  const form = useForm({
    defaultValues: {
      name: speciality.name,
      nameEn: speciality.nameEn ?? '',
      description: speciality.description,
      descriptionEn: speciality.descriptionEn ?? '',
      insuranceCode: speciality.insuranceCode,
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async ({ value }) => {
        try {
          await client.request('PATCH /specialities/{id}', {
            id: speciality.id,
            name: value.name.trim(),
            nameEn: value.nameEn.trim() || undefined,
            description: value.description.trim(),
            descriptionEn: value.descriptionEn.trim() || undefined,
            insuranceCode: value.insuranceCode.trim(),
          });

          queryClient.invalidateQueries({ queryKey: ['specialities'] });

          onOpenChange(false);
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange(isOpen);
    if (!isOpen) {
      form.reset();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Speciality</DialogTitle>
          <DialogDescription>Update speciality information.</DialogDescription>
        </DialogHeader>

        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit();
          }}
        >
          <form.Field
            name="name"
            children={(field) => (
              <div className="space-y-2">
                <Label htmlFor="name">Arabic Name</Label>
                <Input
                  id="name"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="nameEn"
            children={(field) => (
              <div className="space-y-2">
                <Label htmlFor="nameEn">English Name</Label>
                <Input
                  id="nameEn"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="Cardiology"
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="description"
            children={(field) => (
              <div className="space-y-2">
                <Label htmlFor="description">Arabic Description</Label>
                <Textarea
                  id="description"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  rows={4}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="descriptionEn"
            children={(field) => (
              <div className="space-y-2">
                <Label htmlFor="descriptionEn">English Description</Label>
                <Textarea
                  id="descriptionEn"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="Deals with disorders of the heart and blood vessels"
                  rows={4}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="insuranceCode"
            children={(field) => (
              <div className="space-y-2">
                <Label htmlFor="insuranceCode">Insurance Code</Label>
                <Input
                  id="insuranceCode"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="e.g., TC001"
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
              children={([canSubmit, isSubmitting]) => (
                <TheButton
                  type="submit"
                  loading={isSubmitting}
                  disabled={!canSubmit || isSubmitting}
                >
                  {isSubmitting ? 'Updating...' : 'Update'}
                </TheButton>
              )}
            />
          </DialogFooter>

          <form.Subscribe
            selector={(state) => state.errors}
            children={(errors) => <FormMessage errors={errors} />}
          />
        </form>
      </DialogContent>
    </Dialog>
  );
}
